import {assert} from "../util/assert";
import JSZip from "jszip";
import { textFileToString, setStatus, convertToRouteArray, textToHtml } from "./import-utils";
import { computeMileMarker } from "../util/gps";
import { RouteData, Destination, Coordinate, RouteAndImages, RouteImageFiles, RouteFileType } from "../util/route-data";
import { findMostCommon } from "../util/types";
import { getGoogleMyMapsUrlMid } from "./route-refresh-support";

export async function processKmzFile(file: File|ArrayBuffer|NonSharedBuffer, { docLink, filename }: { docLink?: string, filename?: string } = {}): Promise<RouteAndImages> {
  let zip: JSZip|undefined = undefined;
  let kmlFileString: string|null;
  if ((filename && (filename.endsWith('.kml') || filename.endsWith('.xml'))) ||
      (docLink && docLink.includes('&forcekml=1'))) {
    kmlFileString = await textFileToString(file);
  } else {
    try {
      zip = await JSZip.loadAsync(file);
    } catch (e) {
      if (docLink) {
        setStatus('kmz was invalid zip file. Trying kml version instead.');
        return await processKmzLinkUrl(docLink + '&forcekml=1');
      }
      throw e;
    }
    const docKmlFile = zip.files['doc.kml'];
    assert(docKmlFile, "doc.kml not found in kmz.");
    kmlFileString = await docKmlFile.async('string');
  }

  const parser = new DOMParser();
  let kmlDoc: Document|null = parser.parseFromString(kmlFileString, 'text/xml');
  // Free kmlFileString from memory.
  kmlFileString = null;

  const docLinkEl = kmlDoc.querySelector('NetworkLink > Link > href');
  if (docLinkEl) {
    console.log('Found doc link. Fetching real kmz');
    const innerDocLink = docLinkEl.textContent;
    assert(innerDocLink);
    return await processKmzLinkUrl(innerDocLink);
  }

  setStatus('Processing kml file');
  const kmzData = await kmlDocumentToRouteData(kmlDoc, Boolean(docLink));
  // Free from memory.
  kmlDoc = null;

  if (docLink) {
    try {
      // If it's Google My Maps URL, set the link to the viewer instead of the download link.
      // Otherwise, don't make assumptions about the URL and just set it directly.
      const mid = getGoogleMyMapsUrlMid(new URL(docLink));
      if (mid) {
        kmzData.link = `https://www.google.com/maps/d/viewer?mid=${encodeURIComponent(mid)}`;
      } else {
        kmzData.link = docLink;
      }
    } catch {}
  }

  const routeAndImages: RouteAndImages = { routeData: kmzData };
  if (zip) {
    setStatus('Extracting images');
    routeAndImages.images = await extractImagesFromKmzZip(kmzData, zip);
  }
  return routeAndImages;
}

  export async function extractImagesFromKmzZip(kmzData: RouteData, zip: JSZip): Promise<RouteImageFiles> {
    const images: RouteImageFiles = {};
    const imagePaths = new Set(Object.values(kmzData.icons));
    for (const imagePath of imagePaths) {
      const file = zip.files[imagePath];
      if (!file) {
        console.warn('Unable to find image in kmz: ' + imagePath);
        continue;
      }
      images[imagePath] =  await file.async('arraybuffer');
    }
    return images;
  }


async function processKmzLinkUrl(docLink: string): Promise<RouteAndImages> {
  if (!navigator.onLine) {
    throw 'Your device is offline.';
  }
  let response;
  try {
    response = await fetch(docLink);
  } catch (e: any) {
    throw 'Request failed. Check the map\'s share settings and confirm it is ' +
        'set to "Anyone with link" can view.';
  }
  return await processKmzFile(await response.arrayBuffer(), {docLink});
}

async function kmlDocumentToRouteData(kmlDoc: Document, fromUrl: boolean): Promise<RouteData> {
  const docNameEl = kmlDoc.querySelector('name');
  const docName = docNameEl?.textContent;
  assert(docName, 'Unable to find name in KML file.')

  const data: RouteData = {
    icons: {},
    route: [],
    spots: [],
    categories: [],
    name: docName,
    totalDistance: 0,
    importedViaUrl: fromUrl,
    fileType: RouteFileType.KMZ, 
  };

  // The IDs we get in KML are huge, so we generate integer ids.
  // iconIdMap is a string of kml id to internal id.
  const iconIdMap = new Map<string, string>();
  let iconCounter = 0;
  const styles = Array.from(kmlDoc.getElementsByTagName('Style'));
  assert(styles.length, 'Unable to find Styles in kml file.');
  for (const style of styles) {
    const id = style.getAttribute('id');
    assert(id);
    const href = style.querySelector('IconStyle > Icon > href')
    if (!href) {
      continue;
    }
    const internalId = String(iconCounter++);
    iconIdMap.set(id, internalId);
    data.icons[internalId] = href.textContent!;
  }

  const folders = Array.from(kmlDoc.getElementsByTagName('Folder'));
  const foldersByName = new Map<string, Element>();
  let directionsFolder: Element|null = null;
  for (const folder of folders) {
    const nameEl = folder.querySelector('name');
    const name = nameEl?.textContent;
    assert(name);
    foldersByName.set(name, folder);
    const hasLine = folder.querySelector('Placemark > LineString');
    if (hasLine) {
      assert(!directionsFolder, 'Found multiple sets of directions. Only 1 set of directions is supported.')
      directionsFolder = folder;
    }
  }

  assert(directionsFolder, 'Unable to find directions.');
  const directionsCoordsNode = directionsFolder.querySelector('Placemark > LineString > coordinates');
  assert(directionsCoordsNode);
  const directionsCoords =
      directionsCoordsNode.textContent!.trim().split('\n').map(coordTextToNode);

  const {route, totalDistance} = convertToRouteArray(directionsCoords);
  data.route = route;
  data.totalDistance = totalDistance;

  foldersByName.forEach((folder, folderName) => {
    let placeMarks = Array.from(folder.getElementsByTagName('Placemark'))
        .filter(mark => mark.querySelector('Point > coordinates'));
    if (!placeMarks) {
      console.log(folderName + ' has no placemarks');
      return;
    }

    let folderIconIds: string[] = [];
    for (let i = 0; i < placeMarks.length; i++) {
      const placeMark = placeMarks[i];

      const placeName = placeMark.querySelector('name')!.textContent!;
      const descriptionEl = placeMark.querySelector('description');
      const description = textToHtml(descriptionEl?.textContent ?? '');

      let iconId = placeMark.querySelector('styleUrl')!.textContent!.substring(1);
      let iconInternalId = iconIdMap.get(iconId);
      if (!iconInternalId) {
        iconId = iconId + '-normal';
        iconInternalId = iconIdMap.get(iconId);
        if (!iconInternalId) {
          console.log('Missing icon: ' + iconId);
        }
      }
      if (iconInternalId) {
        folderIconIds.push(iconInternalId);
      }
      const coordTextEl = placeMark.querySelector('Point > coordinates');
      assert(coordTextEl?.textContent, 'No coordinates found for ' + placeName);
      const coord = coordTextToNode(coordTextEl.textContent);
      const mile = computeMileMarker(data.route, coord);
      const spot: Destination = {
        name: placeName,
        isRouteStep: directionsFolder === folder,
        categoryName: folderName,
        description,
        coord,
        iconId: iconInternalId,
        mile,
      };
      data.spots.push(spot);
    }

    const iconId = findMostCommon(folderIconIds);
    data.categories.push({
      name: folderName,
      iconId,
    });
  });

  return data;
}

function coordTextToNode(line: string): Coordinate {
  const coords = line.trim().split(',');
  return { lon: Number(coords[0]), lat: Number(coords[1]) };
}